import React, { useState, useEffect } from "react";
import { Download, X, Smartphone } from "lucide-react";

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    // Check if user dismissed before (don't nag)
    const dismissed = sessionStorage.getItem("pwa-dismissed");
    if (dismissed) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show banner after a short delay (not immediately on load)
      setTimeout(() => setShowBanner(true), 3000);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Also show for iOS (Safari doesn't fire beforeinstallprompt)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS && !window.navigator.standalone) {
      setTimeout(() => setShowBanner(true), 5000);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setIsInstalled(true);
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem("pwa-dismissed", "true");
  };

  if (isInstalled || !showBanner) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-3 animate-slideUp" data-testid="pwa-install-banner">
      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-slideUp { animation: slideUp 0.4s ease-out; }
      `}</style>
      <div className="max-w-lg mx-auto bg-[#1E1B4B] rounded-2xl p-4 shadow-2xl border border-[#E83E8C]/20">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] flex items-center justify-center flex-shrink-0">
            <Smartphone size={20} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white">Install DoozyGo App</p>
            {isIOS ? (
              <p className="text-[11px] text-white/60 mt-0.5">
                Tap <span className="inline-flex items-center px-1 py-0.5 bg-white/10 rounded text-white/80 text-[10px] font-medium mx-0.5">
                  Share
                </span> then <span className="inline-flex items-center px-1 py-0.5 bg-white/10 rounded text-white/80 text-[10px] font-medium mx-0.5">
                  Add to Home Screen
                </span>
              </p>
            ) : (
              <p className="text-[11px] text-white/60 mt-0.5">Get quick access, offline support & app-like experience</p>
            )}
          </div>
          <button onClick={handleDismiss} className="text-white/30 hover:text-white/60 p-1" data-testid="pwa-dismiss">
            <X size={16} />
          </button>
        </div>
        {!isIOS && deferredPrompt && (
          <button onClick={handleInstall}
            className="w-full mt-3 py-2.5 rounded-xl bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white text-xs font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            data-testid="pwa-install-btn">
            <Download size={14} /> Install App — It's Free
          </button>
        )}
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
